import { format } from 'd3-format';
import { FC } from 'react';
import { interval } from '../../../feature/feature-services/feature-service/component/ChartDefaultParams';
import { MonitoringDateRange } from '../../monitoring/DateRange-Dropdown-util';

import { FCOFields, FeatureViewFCO, FeatureViewFCOFields, FeatureViewFCOType } from '../../../core/types/fcoTypes';

import { X_AXIS_LABEL } from '../../monitoring/GraphContainer';

// @tecton
import PrometheusMonitoring from '@tecton/ComponentRedesign/Charts/PrometheusMonitoring';
import { PipelineNodeTypes } from '../../../service/Pipeline';
import { filterDataForTableViewsOnly } from './utils';

interface FeatureViewsBelowFreshnessThresholdProps {
  featureViews: FeatureViewFCO[];
  graphType: number;
  monitoringDateRange: MonitoringDateRange;
}

export const getTableQuery = (featureViewIds: string[], relatedFvs: FeatureViewFCO[]): string => {
  // exclude feature view type lists
  const fvTypesToExclude = [FeatureViewFCOType.REALTIME]; // Exclude Realtime FVs since we don't log on it. We only log on the underlying FVs
  const realtimeFVDependantFVIds: string[] = []; //

  const filteredFVIds = featureViewIds?.filter((fvId) => {
    const foundFV = relatedFvs.find((fv) => {
      return fv[FCOFields.ID] === fvId;
    });
    if (foundFV) {
      const canInclude = !fvTypesToExclude.includes(foundFV[FeatureViewFCOFields.FEATURE_VIEW_TYPE]);

      if (!canInclude) {
        // It's a realtime FV which mean we need to get the underlying FVs in the nodes.
        foundFV[FeatureViewFCOFields.PIPELINE]?.allLeafNodes
          ?.filter((node) => {
            return node?.nodeType === PipelineNodeTypes.FEATURE_VIEW;
          })
          ?.forEach((node) => {
            realtimeFVDependantFVIds.push(node?.innerNodeId ?? '');
          });
      }

      // If you are part of the exclusion list, filter it out.
      return canInclude;
    }
    // we can't find the fv so don't show it
    return false;
  });

  return [...filteredFVIds, ...realtimeFVDependantFVIds]
    ?.map((fvId: string, index: number) => {
      // Find the feature view from related features views so we can find if compaction is enabled
      const foundFV = relatedFvs.find((fv) => {
        return fv[FCOFields.ID] === fvId;
      });
      const isFVCompactionEnabled = foundFV ? foundFV[FeatureViewFCOFields.BATCH_COMPACTION_ENABLED] : false;
      // When FV is compaction, we add a .* to the end of the FV Id.
      return `.*_${fvId}${isFVCompactionEnabled ? '.*' : ''}${featureViewIds.length - 1 === index ? '' : '|'}`;
    })
    .join('');
};

const DynamoDBP99Latency: FC<FeatureViewsBelowFreshnessThresholdProps> = ({
  monitoringDateRange,
  featureViews,
  graphType,
}) => {
  const query = `histogram_quantile(0.${graphType}, sum(rate(dynamodb_query_latency_seconds_bucket_rollup_no_pod{
    aws_region=~".*",
    tecton_cluster=~".*"
  }[${interval}])) by (le, table))`;

  const chartTitle = '';
  const yAxisLabel = 'Latency';

  const yTicksCallback = (value: any) => {
    const formatter = format('.2~f'); // 20ms instead of .02 s
    return `${formatter(value * 1000)} ms`;
  };

  const toolTipLabelCallback = (context: any) => {
    const { y } = context.parsed;
    const formatter = format('.2~f');

    const { label } = context.dataset;

    const labelText = `${label}: ${formatter(y * 1000)} ms`;
    return labelText;
  };

  const getMetricLabelCallback = (result: any, label?: string) => {
    // Matches the Feature View ID (which is in the dataset)
    // to the Feature View Name (which is in FeatureViews)
    // Ensures that charts render properly
    const findMatchingId = featureViews.find((featureView) => result.metric.table.includes(featureView[FCOFields.ID]));
    if (findMatchingId) {
      return `P${graphType}:${findMatchingId[FCOFields.NAME]!}`;
    }
    return '';
  };

  const yOverride = {
    title: {
      display: true,
      text: yAxisLabel,
      font: {
        size: 12,
      },
    },
    min: 0,

    ticks: {
      callback: yTicksCallback,
    },
  };

  return (
    <>
      <PrometheusMonitoring
        chartTitle={chartTitle}
        monitoringDateRange={{ ...monitoringDateRange }}
        queries={[{ label: 'd99Latency', query: query }]}
        xAxisLabel={X_AXIS_LABEL}
        yAxisLabel={yAxisLabel}
        yTicksCallback={yTicksCallback}
        yGrace={0}
        toolTipLabelCallback={toolTipLabelCallback}
        yOverride={yOverride}
        parseValue={parseFloat}
        getMetricLabelCallback={getMetricLabelCallback}
        height={160}
        transformData={(data) => {
          return filterDataForTableViewsOnly(data, featureViews);
        }}
      />
    </>
  );
};

export default DynamoDBP99Latency;
